import { A, useNavigate } from "@solidjs/router";
import type { PlaygroundWorkspace } from "contracts/api/playground";
import { createSignal, For, Show } from "solid-js";
import { adminGateProps, isAdmin } from "../../store/admin";
import { api } from "../../store/api";
import { errorMessage } from "../../store/api-client";
import { createAsyncAction } from "../../ui/asyncAction";
import { FormError } from "../../ui/FormError";
import { relativeAge } from "../../ui/format";
import {
  createPlaygroundWorkspaces,
  invalidateWorkspaces,
} from "./playgroundQueries";

/** Workspace index: one playground client per brand experiment. */
export function PlaygroundList() {
  const navigate = useNavigate();
  const workspaces = createPlaygroundWorkspaces();
  const [name, setName] = createSignal("");
  const action = createAsyncAction();

  const create = (event: Event) => {
    event.preventDefault();
    if (!name().trim() || action.busy()) return;
    void action.run(async () => {
      const workspace = await api.playground.createWorkspace({
        name: name().trim(),
      });
      await invalidateWorkspaces();
      navigate(`/playground/${workspace.clientId}`);
    });
  };

  const remove = async (workspace: PlaygroundWorkspace) => {
    if (
      !window.confirm(
        `Delete workspace "${workspace.name}" and all its runs? This cannot be undone.`,
      )
    ) {
      return;
    }
    action.fail("");
    try {
      await api.playground.deleteWorkspace(workspace.clientId);
      await invalidateWorkspaces();
    } catch (cause) {
      action.fail(errorMessage(cause));
    }
  };

  return (
    <main class="landing">
      <div class="landing-bar">
        <span class="landing-brand">Faro</span>
        <span class="landing-tag" />
        <A href="/playground/prompts" class="btn">
          Prompt library
        </A>
        <A href="/" class="btn">
          ← All websites
        </A>
      </div>
      <div class="landing-main">
        <div class="landing-head-row">
          <div>
            <h1 class="landing-h1">Experiment playground</h1>
            <p class="landing-sub pg-lede">
              A safe place to try the audit agent. Each workspace is one
              experiment — a brand voice, the pages you're testing, and every
              run's result. Nothing here touches a client dashboard.
            </p>
          </div>
          <form onSubmit={create} class="unlock-admin-form">
            <input
              class="editor-input"
              placeholder="New workspace name"
              value={name()}
              onInput={(event) => setName(event.currentTarget.value)}
            />
            <button
              class="btn btn-primary"
              type="submit"
              {...adminGateProps(action.busy() || !name().trim(), "create")}
            >
              Create
            </button>
          </form>
        </div>
        <FormError message={action.error()} />
        <div class="panel">
          <Show
            when={workspaces.data}
            fallback={<div class="loading">Loading workspaces…</div>}
          >
            {(data) => (
              <Show
                when={data().workspaces.length > 0}
                fallback={
                  <div class="empty">
                    <div class="empty-title">No workspaces yet</div>
                  </div>
                }
              >
                <table class="tbl tbl-link">
                  <thead>
                    <tr>
                      <th>Workspace</th>
                      <th>Pages</th>
                      <th>Brand voice</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <For each={data().workspaces}>
                      {(workspace) => (
                        <tr
                          class="tbl-row"
                          onClick={() =>
                            navigate(`/playground/${workspace.clientId}`)
                          }
                        >
                          <td>{workspace.name}</td>
                          <td>{workspace.websites.length}</td>
                          <td>
                            <span class="badge">
                              {workspace.tovVersionId ? "active" : "none"}
                            </span>
                          </td>
                          <td>{relativeAge(workspace.createdAt)}</td>
                          <td>
                            <Show when={isAdmin()}>
                              <button
                                type="button"
                                class="btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void remove(workspace);
                                }}
                              >
                                Delete
                              </button>
                            </Show>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </Show>
            )}
          </Show>
        </div>
      </div>
    </main>
  );
}
